import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import { X, Copy } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Modal con QR code di un URL condivisibile.
 * Lo scopo è scansionare il QR da un altro device (es. mobile) per saltare
 * direttamente alla vista filtrata.
 *
 * Props:
 *  - open:  bool
 *  - onClose: () => void
 *  - title: string (etichetta vista)
 *  - url:   string (URL da incorporare nel QR)
 */
export default function ShareQrModal({ open, onClose, title, url }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copiato');
    } catch {
      window.prompt('Copia il link:', url);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="qr-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm bg-canvas border border-ink shadow-2xl"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-line">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-soft">
            Condividi via QR
          </span>
          <button
            type="button"
            onClick={onClose}
            className="text-mute hover:text-ink"
            aria-label="Chiudi"
            data-testid="qr-modal-close"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-6 flex flex-col items-center gap-4">
          <div
            className="font-body text-[14px] text-ink text-center max-w-full truncate"
            title={title}
            data-testid="qr-modal-title"
          >
            {title}
          </div>

          <div className="p-3 bg-white border border-line" data-testid="qr-code-wrapper">
            <QRCodeSVG
              value={url}
              size={220}
              level="M"
              marginSize={0}
              fgColor="#171717"
              bgColor="#FFFFFF"
            />
          </div>

          <div
            className="w-full font-mono text-[10px] text-soft break-all px-2 py-1.5 bg-linesoft border border-line"
            data-testid="qr-modal-url"
          >
            {url}
          </div>

          <button
            type="button"
            onClick={handleCopy}
            className="w-full inline-flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] px-4 py-2 border border-ink text-ink hover:bg-ink hover:text-canvas transition-colors"
            data-testid="qr-modal-copy"
          >
            <Copy size={12} strokeWidth={1.5} />
            Copia link
          </button>

          <p className="font-body text-[11px] text-mute text-center">
            Scansiona dal telefono per aprire la vista filtrata.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}
